import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  indicator: string; // Simple text indicator instead of flag
}

const languages: Language[] = [
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    indicator: 'EN',
  },
  {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    indicator: 'ES',
  },
];

const LanguageSelector: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const currentLanguage = languages.find(lang => i18n.language.startsWith(lang.code)) || languages[0];

  // Auto-detect browser language on first visit
  useEffect(() => {
    const savedLanguage = localStorage.getItem('aura-language');
    
    if (!savedLanguage) {
      // Get browser languages
      const browserLangs = navigator.languages || [navigator.language];
      
      // Find the first supported language
      const supportedLang = browserLangs.find(lang => {
        const langCode = lang.split('-')[0]; // Extract main language code
        return languages.some(supportedLang => supportedLang.code === langCode);
      });
      
      if (supportedLang) {
        const langCode = supportedLang.split('-')[0];
        if (langCode !== i18n.language) {
          i18n.changeLanguage(langCode);
        }
      }
    }
  }, [i18n]);

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    localStorage.setItem('aura-language', languageCode);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-gold-500/20 focus:border-gold-400 transition-all duration-200"
        aria-label={t('language_selector.aria')}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="w-4 h-4" />
        <span className="font-semibold text-xs tracking-wide">
          {currentLanguage.indicator}
        </span>
        <svg 
          className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl backdrop-blur-sm">
          <div className="py-1" role="listbox">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-700/50 transition-all duration-150 ${
                  currentLanguage.code === language.code 
                    ? 'bg-gold-50 dark:bg-gold-900/20 text-gold-900 dark:text-gold-300 font-medium border-l-2 border-gold-500' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}
                role="option"
                aria-selected={currentLanguage.code === language.code}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  currentLanguage.code === language.code 
                    ? 'bg-gold-500 text-white' 
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                }`}>
                  {language.indicator}
                </div>
                <div className="flex flex-col flex-1">
                  <span className="font-medium">{language.nativeName}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t(`language_selector.languages.${language.code}`)}
                  </span>
                </div>
                {currentLanguage.code === language.code && (
                  <div className="w-2 h-2 rounded-full bg-gold-500"></div>
                )}
              </button>
            ))}
          </div>
          
          {/* Subtle footer */}
          <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700">
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
              {t('language_selector.auto_detected')}
            </p>
          </div>
        </div>
      )}

      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default LanguageSelector;
